import { Capacitor } from "@capacitor/core";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { Box, Button, Divider, Link, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { T, useTolgee, useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { ROUTER_BASENAME } from "../../../environment.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import {
  ONBOARDING_BUTTON_HEIGHT,
  ONBOARDING_BUTTON_LABEL,
  ONBOARDING_COLORS,
  ONBOARDING_SMALL_TEXT_BUTTON,
} from "../../features/organisations/onboarding/onboardingDesign.js";
import { useAuth } from "../useAuth.js";
import { GoogleLogo } from "./GoogleLogo.js";
import { SignupHeading } from "./SignupHeading.js";
import {
  buildEmailSignupLoginOptions,
  buildGoogleSignupLoginOptions,
  buildSignupRedirectUri,
  toWebsiteLocale,
} from "./signupAuthOptions.js";
import {
  getOtherSignupRole,
  getSignupRole,
  withSignupRole,
} from "./signupRole.js";
import { SignupSplitLayout } from "./SignupSplitLayout.js";
import { useGoogleOneTap } from "./useGoogleOneTap.js";

const WEBSITE_ORIGIN = "https://meistersystems.de";

/** The legal pages in the visitor's language; the website only has German and English. */
function getLegalLinks(language: string) {
  const websiteLocale = toWebsiteLocale(language);
  return {
    terms: `${WEBSITE_ORIGIN}/${websiteLocale}/terms`,
    privacy: `${WEBSITE_ORIGIN}/${websiteLocale}/privacy`,
  };
}

interface FormValues {
  email: string;
}

// A line of body text followed by a small text button (the switch link, the log-in link).
const ALT_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  columnGap: "4px",
  fontSize: 14,
  lineHeight: 1.45,
  letterSpacing: "normal",
  color: ONBOARDING_COLORS.body,
} as const;

/**
 * Public welcome screen of the self-service signup. It only collects the way
 * in (Google, One Tap or a work email) and hands over to Keycloak, which owns
 * the registration form, credentials and sessions.
 */
export const SignupPage = () => {
  const { auth, isAuthenticated } = useAuth();
  const { t } = useTranslate(["PageSignup", "Global"]);
  const language = useTolgee(["language"]).getLanguage() ?? "";
  const { search } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const role = getSignupRole(search);
  const otherRole = getOtherSignupRole(role);

  const isNative = Capacitor.isNativePlatform();
  // On web the whole page leaves for Keycloak, so the return address must carry
  // the signup parameters. The native adapter returns through its own deep link.
  const redirectUri = isNative
    ? undefined
    : buildSignupRedirectUri({
        origin: window.location.origin,
        basename: ROUTER_BASENAME,
        search,
      });

  const handleFailure = React.useCallback(
    (error: unknown) => {
      Sentry.captureException(error);
      enqueueSnackbar(
        t("Something went wrong. Please try again.", { ns: "Global" }),
        { variant: "error" }
      );
    },
    [enqueueSnackbar, t]
  );

  const startGoogleSignup = (loginHint?: string) => {
    auth
      .login(
        buildGoogleSignupLoginOptions({ language, redirectUri, loginHint })
      )
      .catch(handleFailure);
  };

  useGoogleOneTap({ onAccountPicked: startGoogleSignup });

  const emailLabel =
    role === "CLIENT"
      ? t("Email", { ns: "PageSignup" })
      : t("Work email", { ns: "PageSignup" });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string().trim().label(emailLabel).email().required(),
      }),
    [emailLabel]
  );

  // Keycloak's identity-first login decides what follows: the sign-in steps of
  // an existing account (passkey, password or code by email), or the
  // registration for an unknown email.
  const handleContinue = async ({ email }: FormValues) => {
    try {
      await auth.login(
        buildEmailSignupLoginOptions({
          language,
          redirectUri,
          email: email.trim(),
        })
      );
    } catch (error) {
      handleFailure(error);
    }
  };

  if (isAuthenticated) {
    return <Navigate to={{ pathname: "/", search }} replace />;
  }

  const legalLinks = getLegalLinks(language);

  // The role only lives in the URL, so switching replaces it in place and keeps
  // the other parameters (e.g. a referral token).
  const switchLink = (
    <Button
      variant="text"
      type="button"
      data-testid="signup-switch-role"
      onClick={() => {
        navigate(
          { search: withSignupRole(search, otherRole) },
          { replace: true }
        );
      }}
      sx={ONBOARDING_SMALL_TEXT_BUTTON}
    />
  );

  return (
    <SignupSplitLayout
      signupRole={role}
      footer={
        <Typography component="div" sx={ALT_ROW_SX}>
          <T
            ns="PageSignup"
            keyName="Already have an account? <login>Log in</login>"
            params={{
              login: (
                <Button
                  variant="text"
                  type="button"
                  data-testid="signup-login"
                  onClick={() => {
                    auth.login().catch(handleFailure);
                  }}
                  sx={ONBOARDING_SMALL_TEXT_BUTTON}
                />
              ),
            }}
          />
        </Typography>
      }
    >
      <SignupHeading
        subline={t("Get started. No credit card needed.", { ns: "PageSignup" })}
      />
      <Button
        fullWidth
        variant="outlined"
        startIcon={<GoogleLogo />}
        data-testid="signup-google"
        onClick={() => startGoogleSignup()}
        sx={{
          mt: "36px",
          height: ONBOARDING_BUTTON_HEIGHT,
          px: "22px",
          ...ONBOARDING_BUTTON_LABEL,
          color: ONBOARDING_COLORS.dsNavy,
          bgcolor: "common.white",
          "&:hover": {
            bgcolor: ONBOARDING_COLORS.dsTint,
            borderColor: ONBOARDING_COLORS.dsNavy,
          },
          // The G sits right before the label; both are centred as one group.
          "& .MuiButton-startIcon": { m: "0 8px 0 0" },
        }}
      >
        {t("Continue with Google", { ns: "PageSignup" })}
      </Button>
      <Divider
        sx={{
          my: "20px",
          color: ONBOARDING_COLORS.muted,
          fontSize: 14,
          lineHeight: 1.45,
          letterSpacing: "normal",
          "&::before, &::after": {
            borderColor: ONBOARDING_COLORS.divider,
          },
          "& .MuiDivider-wrapper": { px: "16px", py: 0 },
        }}
      >
        {t("Or", { ns: "Global" })}
      </Divider>
      <Formik<FormValues>
        initialValues={{ email: "" }}
        validationSchema={validationSchema}
        onSubmit={handleContinue}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ isSubmitting }) => (
          <Form noValidate>
            <Box sx={{ mb: "16px" }}>
              <TextField
                name="email"
                type="email"
                label={emailLabel}
                autoComplete="email"
                fullWidth
                slotProps={{
                  htmlInput: { "data-testid": "signup-email" },
                }}
              />
            </Box>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              loading={isSubmitting}
              data-testid="signup-continue"
              sx={{
                mt: 0,
                height: ONBOARDING_BUTTON_HEIGHT,
                px: "22px",
                ...ONBOARDING_BUTTON_LABEL,
              }}
            >
              {t("Continue", { ns: "Global" })}
            </Button>
          </Form>
        )}
      </Formik>
      <Typography
        sx={{
          mt: "20px",
          fontSize: 13,
          lineHeight: 1.5,
          letterSpacing: "normal",
          color: ONBOARDING_COLORS.muted,
          textAlign: "center",
          "& a": {
            color: ONBOARDING_COLORS.navy,
            textDecoration: "none",
            "&:hover": { color: ONBOARDING_COLORS.brandPanel },
          },
        }}
      >
        <T
          ns="PageSignup"
          keyName="By continuing, you agree to the <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>"
          params={{
            terms: (
              <Link
                href={legalLinks.terms}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
            privacy: (
              <Link
                href={legalLinks.privacy}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </Typography>
      <Typography component="div" sx={{ ...ALT_ROW_SX, mt: "28px" }}>
        {role === "CLIENT" ? (
          <T
            ns="PageSignup"
            keyName="Not a property owner? <switch>Sign up as craft business</switch>"
            params={{ switch: switchLink }}
          />
        ) : (
          <T
            ns="PageSignup"
            keyName="Not a craft business? <switch>Sign up as property owner</switch>"
            params={{ switch: switchLink }}
          />
        )}
      </Typography>
    </SignupSplitLayout>
  );
};
